import { CreateFreightClaimInitialState } from "src/pages/CreateFreightClaim/interface";
import { EditOsInitialState } from "src/pages/EditOverShort/interface";
import { trimZeros } from "./genericFunctions";
export interface IGetStoreCreditDetails {
  itemId: string;
  description: string;
  packOrSize: string;
  quantity: number;
  received: number;
  damagedQuantityEaches: number;
  damagedQuantityCases: number;
  overEaches: number;
  overCases: number;
  shortEaches: number;
  shortCases: number;
}

export interface IUpdateFCOS {
  overShortDocId?: string | number;
  itemId?: string;
  storeCreditDetails?: IGetStoreCreditDetails;
  isRandomWeight?: boolean;
  isFCOrOSAdded?: boolean;
  fCOrOSTooltip?: string;
  fCOrOSTooltipTranslationProps?: object;
}

export interface IRemoveStoreCreditProperties {
  isFCOrOSAdded: boolean;
  fCOrOSTooltip: string;
  overShortDocId?: string;
  storeCreditDetails: IGetStoreCreditDetails;
  itemId?: string;
  fCOrOSTooltipTranslationProps?: object;
}

export type IStoreDetailsType = "O/S" | "FC";

export interface IRowDataStoreCreditDetails {
  receivedQuantity?: number | string;
  received?: number | string;
  isFCOrOSAdded: boolean;
  fCOrOSTooltip: string;
  fCOrOSTooltipTranslationProps?: object;
}

export interface IStoreEaches {
  storeCreditDetails?: IGetStoreCreditDetails;
  isRandomWeight: boolean;
  packOrSize: string;
  qtyShipped: string;
  received: string;
}

export interface IValidQtyValue {
  isRandomWeight?: boolean;
  pack?: string;
  received?: string;
  totalEaches?: number;
  receivedEaches?: number;
  packOrSize?: string;
}
export interface ITypeFieldMap {
  damagedQuantityEaches: number;
  damagedQuantityCases: number;
  shortEaches: number;
  shortCases: number;
  qtyShipped: number;
  received: number;
  totalEaches: number;
  receivedEaches: number;
}

export const typeFieldMap = {
  FC: ["shortCases", "shortEaches", "totalEaches"],
  "O/S": ["damagedQuantityCases", "damagedQuantityEaches", "receivedEaches"]
};

export const isFCOrOSAdded = (
  storeCreditDetails: IGetStoreCreditDetails,
  type: IStoreDetailsType
): boolean => {
  if (!storeCreditDetails) {
    return false;
  }
  const fields = typeFieldMap[type];
  return fields
    ? !!storeCreditDetails[fields[0] as keyof IGetStoreCreditDetails] ||
        !!storeCreditDetails[fields[1] as keyof IGetStoreCreditDetails]
    : false;
};

export const getFCOrOSTooltip = (
  item: {
    overShortDocId?: string | number;
    itemId?: string;
    storeCreditDetails?: IGetStoreCreditDetails;
    isRandomWeight?: boolean;
    packOrSize?: string;
    qtyShipped?: string;
  },
  type: IStoreDetailsType
) => {
  const { overShortDocId, itemId, storeCreditDetails } = item;

  if (!storeCreditDetails || !overShortDocId) {
    return { text: "", props: {} };
  }

  const fields = typeFieldMap[type];
  if (!fields) {
    return { text: "", props: {} };
  }

  const quantityCases =
    storeCreditDetails[fields[0] as keyof IGetStoreCreditDetails] || 0;
  const quantityEaches =
    storeCreditDetails[fields[1] as keyof IGetStoreCreditDetails] || 0;

  if (!quantityCases && !quantityEaches) {
    return { text: "", props: {} };
  }

  return {
    text: "storeCredit.tooltipMessage",
    props: {
      typeLabel: type === "FC" ? "FC, O/S" : "O/S, FC",
      overShortDocId,
      itemId: trimZeros(itemId),
      quantityCases,
      quantityEaches
    }
  };
};

export const updatePoItemsWithStoreCreditDetails = (
  itemId: string | number,
  storeCreditDetails: IGetStoreCreditDetails[]
) =>
  storeCreditDetails?.find(
    (x: { itemId: string | number }) => x.itemId === itemId
  );

export const mapPoItemsWithStoreCreditDetails = (
  poItems: IUpdateFCOS[],
  state: CreateFreightClaimInitialState | EditOsInitialState,
  type: IStoreDetailsType
) => {
  const serializedData =
    Object.keys(state).length > 0 ? JSON.parse(JSON.stringify(state)) : {};
  return poItems?.map((item: IUpdateFCOS) => {
    item.overShortDocId = serializedData.overShortDocId;
    item.storeCreditDetails = updatePoItemsWithStoreCreditDetails(
      item.itemId,
      serializedData.getStoreCreditDetails
    );
    const getToolTipEntries = getFCOrOSTooltip(item, type);
    item.isFCOrOSAdded = isFCOrOSAdded(item.storeCreditDetails, type);
    item.fCOrOSTooltip = getToolTipEntries?.text;
    item.fCOrOSTooltipTranslationProps = getToolTipEntries?.props;
    return item;
  });
};

export const removeStoreCreditProperties = (
  element: IRemoveStoreCreditProperties
) => {
  const {
    isFCOrOSAdded,
    fCOrOSTooltip,
    storeCreditDetails,
    overShortDocId,
    fCOrOSTooltipTranslationProps,
    ...rest
  } = element;
  return rest;
};

export const calculateEaches = (
  item: IStoreEaches,
  type: IStoreDetailsType
): number => {
  const [casesField, eachesField] = typeFieldMap[type];
  const qtyShipped = +item?.qtyShipped || +item?.received || 0;
  const packSize = +item.packOrSize.split("/")[0];
  const { storeCreditDetails } = item;
  const adjustedCases = +(
    storeCreditDetails?.[casesField as keyof IGetStoreCreditDetails] || 0
  );
  const adjustedEaches = +(
    storeCreditDetails?.[eachesField as keyof IGetStoreCreditDetails] || 0
  );
  const shortValues = packSize * adjustedCases + adjustedEaches;
  const shippedValue = item.isRandomWeight ? qtyShipped : packSize * qtyShipped;

  return shippedValue - shortValues;
};

export const isValidQuantity = (
  quantityCases: string,
  quantityEaches: string,
  value: IValidQtyValue,
  type: IStoreDetailsType
): boolean => {
  const fields = typeFieldMap[type];
  const pack = +value?.pack || 0;
  const totalQuantity =
    (Number(quantityCases) * pack || 0) + Number(quantityEaches);
  const totalEaches = value[fields[2] as keyof IValidQtyValue] as number;
  return totalQuantity > totalEaches;
};

// To get the shipper item of the component items
export const getShipperItems = (selectedItems: any[], allItems: any[]) => {
  const palletModIdsFromSelectedItems = selectedItems.map(
    (item: any) => item.palletModId
  );

  const shipperItemsOfSelectedPoItems = allItems
    .filter((item) => palletModIdsFromSelectedItems.includes(item.itemId))
    .map(removeStoreCreditProperties);

  return shipperItemsOfSelectedPoItems;
};
